import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Form, Row, Col } from 'react-bootstrap';
import { Button, ButtonGroup, InputGroup } from 'react-bootstrap';
import 'react-datepicker/dist/react-datepicker.css';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import HeaderQuoteAi from './headerQuoteAi';
import SidebarQuoteAi from '../quote/sidebarQuoteAi';
import RightHeaderPage from './rightHeader';

const DeductiblePage = () => {
  const navigate = useNavigate();
  const gridRef = useRef();

  // Edit icon SVG
  const EditIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      fill="currentColor"
      className="bi bi-pencil-square"
      viewBox="0 0 16 16"
    >
      <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z" />
      <path
        fillRule="evenodd"
        d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5v11z"
      />
    </svg>
  );

  // Delete icon SVG
  const DeleteIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      fill="currentColor"
      className="bi bi-trash"
      viewBox="0 0 16 16"
    >
      <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z" />
      <path
        fillRule="evenodd"
        d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"
      />
    </svg>
  );

  // Column Definitions
  const [columnDefs] = useState([
    { field: 'state', headerName: 'State', flex: 1, sortable: true, filter: true },
    {
      field: 'deductibleType',
      headerName: 'Deductible Type',
      flex: 1,
      sortable: true,
      filter: true,
    },
    {
      field: 'deductibleAmount',
      headerName: 'Deductible Amount',
      flex: 1,
      sortable: true,
      filter: true,
      valueFormatter: (params) => (params.value ? `$${params.value}` : ''),
    },
    {
      field: 'deductiblePercentage',
      headerName: 'Deductible Percentage',
      flex: 1,
      sortable: true,
      filter: true,
      valueFormatter: (params) => (params.value ? `${params.value}%` : ''),
    },
    {
      headerName: 'Action',
      width: 100,
      sortable: false,
      filter: false,
      cellStyle: { display: 'flex', justifyContent: 'center', alignItems: 'center' },
      cellRenderer: (params) => {
        return (
          <div className="action-buttons-container" style={{ display: 'flex', gap: '8px' }}>
            <button
              className="icon-button"
              onClick={() => handleEdit(params.data)}
              title="Edit"
              style={{
                background: 'none',
                border: 'none',
                padding: '2px',
                cursor: 'pointer',
                color: '#333', // Bootstrap primary color
              }}
            >
              <EditIcon />
            </button>
            <button
              className="icon-button"
              onClick={() => handleDelete(params.data)}
              title="Delete"
              style={{
                background: 'none',
                border: 'none',
                padding: '2px',
                cursor: 'pointer',
                color: '#333', // Bootstrap danger color
              }}
            >
              <DeleteIcon />
            </button>
          </div>
        );
      },
    },
  ]);

  // Sample Data
  const [rowData, setRowData] = useState([
    {
      id: 1,
      state: 'California',
      deductibleType: 'Medical',
      deductibleAmount: 1000,
      deductiblePercentage: 10,
    },
    {
      id: 2,
      state: 'Texas',
      deductibleType: 'Indemnity',
      deductibleAmount: 2000,
      deductiblePercentage: 15,
    },
    {
      id: 3,
      state: 'New York',
      deductibleType: 'Others',
      deductibleAmount: 1500,
      deductiblePercentage: 12,
    },
  ]);

  // Handler functions
  const handleEdit = (data) => {
    console.log('Edit row:', data);
    // Implement your edit logic here
  };

  const handleDelete = (data) => {
    console.log('Delete row:', data);
    const newRowData = rowData.filter((row) => row.id !== data.id);
    setRowData(newRowData);
  };

  const defaultColDef = {
    resizable: true,
    suppressMovable: true,
  };

  return (
    <>
      <div id="quoteAi">
        <HeaderQuoteAi />
        <div className="QuoteDashboard">
          <div
            className="main"
            //  style={{ marginTop: '2rem' }}
          >
            <div className="pageformBloack">
              <div className=" container-fluid ">
                <div className="row">
                  <div className="col-12 col-md-12 col-lg-2">
                    <div className="sidebar-visible">
                      <SidebarQuoteAi />
                    </div>
                  </div>

                  <div className="col-12 col-md-12 col-lg-8 gx-2 main-quotescroll">
                    <div className="whiteBoxCont ">
                      <div className="quote_buttongroup">
                        <div className="form_action_buttons mb-2">
                          <ButtonGroup>
                            <Button
                              variant="outline-back"
                              className="button-gap"
                              onClick={() => navigate('/quoteAI/RatingInformationPage')}
                            >
                              Back
                            </Button>
                            <Button
                              variant="outline-next"
                              className="button-gap"
                              onClick={() => navigate('/quoteAI/LossDetailPage')}
                            >
                              Next
                            </Button>
                          </ButtonGroup>
                          <ButtonGroup>
                            <Button variant="outline-cancel" className="button-gap">
                              Cancel
                            </Button>
                            <Button variant="primary-save" className="button-gap">
                              Save
                            </Button>
                          </ButtonGroup>
                        </div>
                      </div>
                      <div className="scrollable-content">
                        <div className="formcard-container mrgTop ">
                          {/* Insured Information Section */}
                          <Card className="mb-4 formpage-card">
                            <Card.Header className="formpage-card-header">
                              Deductibles
                              <div className=" quote_buttongroup  ">
                                <Button
                                  variant="outline-next"
                                  size="sm"
                                  className="button-icon"
                                  style={{
                                    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.15)',
                                  }}
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="16"
                                    height="16"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      d="M12 5v14M5 12h14"
                                      className="add_Svg"
                                      stroke="#7C67AD"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                    />
                                  </svg>
                                  Add New
                                </Button>
                              </div>
                            </Card.Header>
                            <Card.Body>
                              <Row className="mb-4">
                                <Col xs={12}>
                                  <h4 className="step-header">Deductibles</h4>
                                </Col>

                                {/* Medical Deductible */}
                                <Col xs={12} sm={12} md={6} lg={4} className="mb-3">
                                  <Form.Group className="form-section-group">
                                    <Form.Label>Medical</Form.Label>

                                    <Row className="value-label-row">
                                      <Col
                                        xs={12}
                                        sm={6}
                                        md={3}
                                        className="d-flex align-items-center"
                                      >
                                        <Form.Check
                                          type="radio"
                                          name="medical"
                                          className="me-2"
                                          id="yes-radio"
                                        />
                                        <Form.Label htmlFor="yes-radio" className="mb-0">
                                          Yes
                                        </Form.Label>
                                      </Col>
                                      <Col
                                        xs={12}
                                        sm={6}
                                        md={3}
                                        className="d-flex align-items-center"
                                      >
                                        <Form.Check
                                          type="radio"
                                          name="medical"
                                          className="me-2"
                                          id="no-radio"
                                        />
                                        <Form.Label htmlFor="no-radio" className="mb-0">
                                          No
                                        </Form.Label>
                                      </Col>
                                    </Row>
                                  </Form.Group>
                                </Col>

                                {/* Indemnity Deductible */}
                                <Col xs={12} sm={12} md={6} lg={4} className="mb-3">
                                  <Form.Group className="form-section-group">
                                    <Form.Label>Indemnity</Form.Label>

                                    <Row className="value-label-row">
                                      <Col
                                        xs={12}
                                        sm={6}
                                        md={3}
                                        className="d-flex align-items-center"
                                      >
                                        <Form.Check
                                          type="radio"
                                          name="indemnity"
                                          className="me-2"
                                          id="yes-radio"
                                        />
                                        <Form.Label htmlFor="yes-radio" className="mb-0">
                                          Yes
                                        </Form.Label>
                                      </Col>
                                      <Col
                                        xs={12}
                                        sm={6}
                                        md={3}
                                        className="d-flex align-items-center"
                                      >
                                        <Form.Check
                                          type="radio"
                                          name="indemnity"
                                          className="me-2"
                                          id="no-radio"
                                        />
                                        <Form.Label htmlFor="no-radio" className="mb-0">
                                          No
                                        </Form.Label>
                                      </Col>
                                    </Row>
                                  </Form.Group>
                                </Col>

                                {/* Others Deductible */}
                                <Col xs={12} sm={12} md={6} lg={4} className="mb-3">
                                  <Form.Group className="form-section-group">
                                    <Form.Label>Others</Form.Label>
                                    <Row className="value-label-row">
                                      <Col
                                        xs={12}
                                        sm={6}
                                        md={3}
                                        className="d-flex align-items-center"
                                      >
                                        <Form.Check
                                          type="radio"
                                          name="others"
                                          className="me-2"
                                          id="yes-radio"
                                        />
                                        <Form.Label htmlFor="yes-radio" className="mb-0">
                                          Yes
                                        </Form.Label>
                                      </Col>
                                      <Col
                                        xs={12}
                                        sm={6}
                                        md={3}
                                        className="d-flex align-items-center"
                                      >
                                        <Form.Check
                                          type="radio"
                                          name="others"
                                          className="me-2"
                                          id="no-radio"
                                        />
                                        <Form.Label htmlFor="no-radio" className="mb-0">
                                          No
                                        </Form.Label>
                                      </Col>
                                    </Row>
                                  </Form.Group>
                                </Col>

                                {/* Amount Field */}
                                <Col xs={12} sm={12} md={6} lg={6} className="mb-3">
                                  <Form.Group className="form-section-group">
                                    <Form.Label>Amount</Form.Label>
                                    <InputGroup>
                                      <InputGroup.Text>$</InputGroup.Text>
                                      <Form.Control
                                        type="number"
                                        placeholder="Enter amount"
                                        min="0"
                                        step="0.01"
                                      />
                                    </InputGroup>
                                  </Form.Group>
                                </Col>

                                {/* Percentage Field */}
                                <Col xs={12} sm={12} md={6} lg={6} className="mb-3">
                                  <Form.Group className="form-section-group">
                                    <Form.Label>Percentage</Form.Label>
                                    <Form.Control
                                      type="number"
                                      placeholder="Enter percentage"
                                      min="0"
                                      max="100"
                                      step="0.01"
                                    />
                                  </Form.Group>
                                </Col>
                              </Row>
                              <div className=" quote_buttongroup d-flex justify-content-between mb-2">
                                <h4 className="step-header">State Deductibles</h4>
                              </div>

                              <div
                                className="ag-theme-balham quote-grid-style"
                                style={{
                                  width: '100%',
                                  border: '1px solid #ddd',
                                  borderRadius: '8px',
                                  overflow: 'hidden',
                                }}
                              >
                                <AgGridReact
                                  ref={gridRef}
                                  rowData={rowData}
                                  columnDefs={columnDefs}
                                  defaultColDef={defaultColDef}
                                  animateRows={true}
                                  rowSelection="multiple"
                                  pagination={true}
                                  paginationPageSize={10}
                                  domLayout="autoHeight"
                                />
                              </div>
                            </Card.Body>
                          </Card>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-12 col-md-12 col-lg-2">
                    <div className="sticky-right-top">
                      <RightHeaderPage />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
export default DeductiblePage;
